import { TextField } from "@buildblockinc/buildblock-design-system";
import { Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { KEYWORD_MAX_CODE_POINTS, countCodePoints, normalizeSearchKeyword } from "../model/dailyLogSearchState";

export const KEYWORD_APPLY_DEBOUNCE_MS = 300;

interface DailyLogSearchKeywordInputProps {
  /** URL에 적용된 검색어 */
  appliedKeyword: string;
  onApply: (keyword: string, trigger: "typing" | "enter" | "clear") => void;
}

/**
 * 입력 중 문자열은 로컬에 두고, 300ms debounce 또는 Enter로 적용한다.
 * IME 조합 중에는 적용하지 않아 한글 입력 도중 요청이 나가지 않는다.
 */
export function DailyLogSearchKeywordInput({ appliedKeyword, onApply }: DailyLogSearchKeywordInputProps) {
  const [value, setValue] = useState(appliedKeyword);
  const [isComposing, setIsComposing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  // 부모가 매 렌더 새 콜백을 넘겨도 debounce 타이머가 다시 시작되지 않게 최신 콜백만 참조한다.
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  /** 이 입력창이 적용 요청했지만 아직 반영되지 않은 검색어들. URL 반영이 비동기라 그 사이 입력이 이어질 수 있다 */
  const pendingRequestedKeywordsRef = useRef<string[]>([]);

  const requestApply = useCallback((keyword: string, trigger: "typing" | "enter" | "clear") => {
    pendingRequestedKeywordsRef.current.push(keyword);
    onApplyRef.current(keyword, trigger);
  }, []);

  const normalizedValue = normalizeSearchKeyword(value);
  const isTooLong = countCodePoints(normalizedValue) > KEYWORD_MAX_CODE_POINTS;

  // 뒤로 가기·Reset all 등 밖에서 적용 검색어가 바뀌면 입력을 맞춘다.
  // 이 입력창이 요청한 검색어가 반영된 것이면 그 사이 이어서 친 글자·IME 조합을 지우지 않도록 두고,
  // 같은 의미의 입력(앞뒤 공백 등)도 caret 보존을 위해 두지 않는다.
  useEffect(
    function syncInputWithAppliedKeyword() {
      const pending = pendingRequestedKeywordsRef.current;
      const ownRequestIndex = pending.indexOf(appliedKeyword);
      if (ownRequestIndex >= 0) {
        pendingRequestedKeywordsRef.current = pending.slice(ownRequestIndex + 1);
        return;
      }
      pendingRequestedKeywordsRef.current = [];
      setValue((current) => (normalizeSearchKeyword(current) === appliedKeyword ? current : appliedKeyword));
    },
    [appliedKeyword],
  );

  const cancelScheduledApply = () => clearTimeout(timerRef.current);

  const applyNow = (keyword: string, trigger: "enter" | "clear") => {
    cancelScheduledApply();
    if (keyword === appliedKeyword) return;
    requestApply(keyword, trigger);
  };

  useEffect(
    function applyKeywordAfterTypingPause() {
      if (isComposing || isTooLong || normalizedValue === appliedKeyword) return;
      const timer = setTimeout(() => requestApply(normalizedValue, "typing"), KEYWORD_APPLY_DEBOUNCE_MS);
      timerRef.current = timer;
      return () => clearTimeout(timer);
    },
    [isComposing, isTooLong, normalizedValue, appliedKeyword, requestApply],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.nativeEvent.isComposing || isTooLong) return;
    event.preventDefault();
    applyNow(normalizedValue, "enter");
  };

  const handleClear = () => {
    setValue("");
    applyNow("", "clear");
  };

  return (
    <TextField invalid={isTooLong}>
      <TextField.Input
        // type="search"는 브라우저 기본 지우기 버튼이 Clear keyword 버튼과 겹쳐 text + searchbox role을 쓴다.
        type="text"
        role="searchbox"
        aria-label="Search daily logs"
        enterKeyHint="search"
        placeholder="Search daily log text"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        prefix={<Search width={16} height={16} className="text-gray-700" aria-hidden="true" />}
        suffix={
          value && (
            <button type="button" onClick={handleClear} aria-label="Clear keyword">
              <X width={16} height={16} className="text-gray-700" />
            </button>
          )
        }
      />
      {isTooLong && <TextField.ErrorText>{`Use ${KEYWORD_MAX_CODE_POINTS} characters or fewer.`}</TextField.ErrorText>}
    </TextField>
  );
}
